import {
  CategoryName,
  DbId,
  DisplayModeName,
  TGUID,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BuiltinCaptionType, CaptionType } from '../../models/caption/Caption';
import CommandViewModel, {
  CommandListEntryType,
  CommandName,
} from '../../services/commands/CommandViewModel';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';

export const BalanceCategory = 'BalanceReports' as const;
export const BalanceIndexValuesCategory = 'BalanceIndexValues' as const;
export const BalanceIndexSubValuesCategory = 'BalanceIndexSubValues' as const;
export const BalanceCheckReportsCategory = 'BalanceCheckReports' as const;
export const BalanceCheckReportsSpecsCategory =
  'BalanceCheckReportsSpecs' as const;
export const BalanceDisplayMode = 'system' as const;
export const BalanceAction = {
  CALCULATE: { name: 'calc', confirm: true, needsRefresh: true },
  CHECK: { name: 'check', confirm: true, needsRefresh: false },
  SHOW_CHECK: { name: 'showCheck', confirm: false, needsRefresh: false },
  EDIT_VALUES: { name: 'editValues', confirm: false, needsRefresh: false },
  GET_VALUES: { name: 'getValues', confirm: false, needsRefresh: false },
  STORE_VALUES: { name: 'storeValues', confirm: false, needsRefresh: false },
  GET_CONS_STATE: { name: 'getConsState', confirm: false, needsRefresh: false },
  EXCEL_EXPORT: { name: 'downloadXLSX', confirm: true, needsRefresh: false },
} as const;

export const BalanceCommandsModel: CommandViewModel[] = [
  {
    type: CommandListEntryType.COMMAND,
    name: BalanceAction.STORE_VALUES.name,
    icon: 'floppy',
    caption: {
      type: CaptionType.BUILT_IN,
      caption: BuiltinCaptionType.STORE_VALUES,
    },
    available: true,
  },
  { type: CommandListEntryType.SEPARATOR },
  {
    type: CommandListEntryType.COMMAND,
    name: BalanceAction.CALCULATE.name,
    icon: 'calculate',
    caption: {
      type: CaptionType.BUILT_IN,
      caption: BuiltinCaptionType.CALCULATE,
    },
    available: true,
  },
  {
    type: CommandListEntryType.COMMAND,
    name: BalanceAction.CHECK.name,
    icon: 'plan-time-unlock',
    caption: {
      type: CaptionType.BUILT_IN,
      caption: BuiltinCaptionType.CHECK,
    },
    available: true,
  },
  {
    type: CommandListEntryType.COMMAND,
    name: BalanceAction.SHOW_CHECK.name,
    icon: 'fact-time-unlock',
    caption: {
      type: CaptionType.BUILT_IN,
      caption: BuiltinCaptionType.SHOW_CHECK,
    },
    available: true,
  },
  { type: CommandListEntryType.SEPARATOR },
  {
    type: CommandListEntryType.COMMAND,
    name: BalanceAction.EXCEL_EXPORT.name,
    icon: 'excel',
    caption: {
      type: CaptionType.BUILT_IN,
      caption: BuiltinCaptionType.EXCEL_EXPORT,
    },
    available: true,
  },
];

type BalanceValue = string | number;
export type BalanceValues = {
  [code: string]: BalanceValue;
};

export type BalanceIndexes = Array<{
  code: string;
  sheet: number;
  row: number;
  col: number;
  category?: CategoryName;
  displayMode?: DisplayModeName;
  inParam?: string;
  outParam?: string;
  extraDict?: string;
  manualFill?: boolean;
  isMandatory?: boolean;
  validation?: {
    checkRegex: string;
    errorMessage: string;
    minVal?: number | Date | null;
    maxVal?: number | Date | null;
  };
  linkedIndex?: {
    code: string;
    col: number;
  };
  multiSelect?: boolean;
}>;

export type BalanceRecords = {
  [id: string]: {
    startRow: number;
    order: Record<string, number>;
    values: BalanceValues[];
  };
};

export interface BalanceOptions {
  isReadOnly: boolean;
  isCons: boolean;
  isShowSavingDialog: boolean;
  bgColor?: string;
  activeCellColor?: string;
  isValidation: boolean;
  direction: string;
}

export interface BalanceStateEntity {
  datasetId: UniqueId;
  reportId: DbId;
  subReportId: DbId;
  streamId: TGUID;
  templateId: DbId;
  title: string;
  indexes: BalanceIndexes;
  records: BalanceRecords;
  values: BalanceValues;
  options: BalanceOptions;
  isChanged: boolean;
  showConfirm: boolean;
  command: CommandName | null;
  isRefresh: boolean;
  isCalculate: boolean;
  pendingCount: number;
}
export type BalanceState = Record<UniqueId, BalanceStateEntity>;

const initialState: BalanceState = {};

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    open: pendingAction<
      BalanceState,
      {
        datasetId: UniqueId;
        streamId: UniqueId;
        templateId: DbId;
        reportId: DbId;
        subReportId: DbId;
        formName: string;
        title: string;
        indexes: BalanceIndexes;
        records: BalanceRecords;
        values: BalanceValues;
        options: BalanceOptions;
      }
    >(),
    openSuccess: successAction<
      BalanceState,
      {
        id: UniqueId;
        streamId: TGUID;
        templateId: DbId;
        reportId: DbId;
        subReportId: DbId;
        formName: string;
        title: string;
        indexes: BalanceIndexes;
        records: BalanceRecords;
        values: BalanceValues;
        options: BalanceOptions;
      }
    >(
      (
        state,
        {
          payload: {
            id,
            streamId,
            templateId,
            reportId,
            subReportId,
            title,
            indexes,
            records,
            values,
            options,
          },
        }
      ) => {
        state[id] = {
          datasetId: '',
          streamId,
          templateId,
          reportId,
          subReportId,
          title,
          indexes,
          records,
          values,
          options,
          isChanged: false,
          showConfirm: false,
          command: null,
          isRefresh: false,
          isCalculate: false,
          pendingCount: 1,
        };
      }
    ),
    openFailure: failureAction(),
    setDataset(
      state,
      {
        payload: { id, datasetId },
      }: PayloadAction<{
        id: UniqueId;
        datasetId: UniqueId;
      }>
    ) {
      if (state[id]) {
        state[id].datasetId = datasetId;
      }
    },
    setChanges(
      state,
      {
        payload: { id, isChanged, indexes, changedValues, records },
      }: PayloadAction<{
        id: UniqueId;
        isChanged: boolean;
        changedValues?: BalanceValues;
        records?: BalanceRecords;
        indexes?: BalanceIndexes;
      }>
    ) {
      state[id].isChanged = isChanged;
      if (changedValues) {
        for (const [code, value] of Object.entries(changedValues)) {
          state[id].values[code] = value;
        }
      }
      if (records) {
        for (const [group, record] of Object.entries(records)) {
          state[id].records[group] = record;
        }
      }
      if (indexes) {
        state[id].indexes = indexes;
      }
      state[id].isRefresh = false;
    },
    showConfirm(
      state,
      {
        payload: { id, isShow },
      }: PayloadAction<{ id: UniqueId; isShow: boolean }>
    ) {
      state[id].showConfirm = isShow;
      if (!isShow) {
        state[id].command = null;
      }
    },
    initiateCommand(
      state,
      {
        payload: { id, command },
      }: PayloadAction<{ id: UniqueId; command: CommandName }>
    ) {
      state[id].command = command;
    },
    initiateCommandSuccess: successAction(),
    initiateCommandFailure: failureAction(),
    executeCommand: pendingAction<
      BalanceState,
      { id: UniqueId; command: CommandName; isSave: boolean }
    >((state, { payload: { id } }) => {
      state[id].command = null;
    }),
    executeCommandSuccess: successAction(),
    executeCommandFailure: failureAction(),
    refresh: pendingAction<BalanceState, { datasetId: UniqueId }>(),
    refreshSuccess: successAction<
      BalanceState,
      {
        id: UniqueId;
        values: BalanceValues;
        records: {
          [id: string]: { values: BalanceValues[] };
        };
      }
    >((state, { payload: { id, values, records } }) => {
      state[id].isChanged = false;
      state[id].values = values;
      for (const [group, record] of Object.entries(records)) {
        state[id].records[group] = {
          ...state[id].records[group],
          values: record.values,
        };
      }
      state[id].isRefresh = true;
    }),
    refreshFailure: failureAction(),
    close: pendingAction<BalanceState, { id: UniqueId; isSave: boolean }>(),
    closeSuccess: successAction<
      BalanceState,
      { id: UniqueId; datasetId: UniqueId }
    >((state, { payload: { id } }) => {
      delete state[id];
    }),
    closeFailure: failureAction(),
    showConsState: pendingAction<
      BalanceState,
      {
        id: UniqueId;
        indexCode: string;
        isSection: boolean;
        groupCode: string | undefined;
        rowNum: number | null;
      }
    >(),
    showConsStateSuccess: successAction(),
    showConsStateFailure: failureAction(),
    showIndex: pendingAction<BalanceState, { indexCode: string }>(),
    showIndexSuccess: successAction(),
    showIndexFailure: failureAction(),
    setCalculate(
      state,
      {
        payload: { id, isCalculate },
      }: PayloadAction<{ id: UniqueId; isCalculate: boolean }>
    ) {
      state[id].isCalculate = isCalculate;
    },
    setPending(
      state,
      {
        payload: { id, isPending },
      }: PayloadAction<{ id: UniqueId; isPending: boolean }>
    ) {
      if (isPending) {
        state[id].pendingCount++;
      } else {
        state[id].pendingCount = Math.max(state[id].pendingCount - 1, 0);
      }
    },
  },
});

export const balanceReducer = balanceSlice.reducer;

export default balanceSlice.actions;
